/**
 * @class Modal
 *
 * @method constructor
 *     @param {HTMLElement} el
 *
 * @method toggleModal
 * @method init
 *
 * Create a modal
 */

const selectors = {
  close: '.js-modal-close',
  expandable: '.js-expandable',
};

const classes = {
  open: 'is-open',
  visible: 'is-visible',
  locked: 'is-locked',
  dark: 'is-dark',
};

export default class Modal {
  constructor(el) {
    this.el = el;
    this.canLockScroll = el.dataset.scrollLock;
    this.canClickOutside = el.dataset.clickOutside;
    this.darkBg = el.dataset.darkBg;
    this.closes = el.querySelectorAll(selectors.close);
    this.triggers = document.querySelectorAll(
      `[data-modal-trigger="${this.el.id}"]`,
    );
    this.expandables = el.querySelectorAll(selectors.expandable);
    this.isOpen = false;

    this.onCloseClick = this.onCloseClick.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onClickOutside = this.onClickOutside.bind(this);

    this.init();
  }

  toggleModal(isOpen) {
    if (isOpen && !this.isOpen) {
      this.openModal();
    } else if (!isOpen && this.isOpen) {
      this.closeModal();
    }

    this.isOpen = isOpen;
  }

  lockScroll() {
    document.body.classList.add(classes.locked);
    window.lenis.stop();
  }

  unlockScroll() {
    document.body.classList.remove(classes.locked);
    window.lenis.start();
  }

  openModal() {
    this.el.classList.add(classes.open);
    this.el.setAttribute('aria-hidden', false);
    if (this.canLockScroll) {
      this.lockScroll();
    }
    if (this.darkBg) {
      document.body.classList.add(classes.dark);
    }
    this.isOpen = true;

    this.expandables.forEach(expandable => {
      if (!expandable.expandable) return;
      expandable.expandable.setHeight();
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.el.classList.add(classes.visible);
      });
    });

    setTimeout(() => {
      this.el.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }, 500);
  }

  closeModal() {
    this.el.classList.remove(classes.visible);
    this.isOpen = false;

    setTimeout(() => {
      this.el.classList.remove(classes.open);
      this.el.setAttribute('aria-hidden', true);

      if (!this.el.parentElement.closest('.js-modal') && this.canLockScroll)
        this.unlockScroll();
      document.body.classList.remove(classes.dark);

      this.expandables.forEach(expandable => {
        if (!expandable.expandable) return;
        expandable.expandable.toggleContent(false);
      });
    }, 500);
  }

  onCloseClick() {
    this.closeModal();
  }

  onTriggerClick() {
    this.openModal();
  }

  onKeyUp(e) {
    if (e.key === 'Escape') this.closeModal();
  }

  onClickOutside(e) {
    if (!this.canClickOutside) return;
    let isOutsideTrigger = true;
    this.triggers.forEach(trigger => {
      if (trigger.contains(e.target)) isOutsideTrigger = false;
    });
    const isOutsideModal = e.target !== this.el && !this.el.contains(e.target);
    if (isOutsideTrigger && isOutsideModal && this.isOpen) {
      e.preventDefault();
      this.closeModal();
    }
  }

  init() {
    this.closes.forEach(close => {
      close.addEventListener('pointerup', this.onCloseClick);
    });

    window.addEventListener('keyup', this.onKeyUp);

    this.triggers.forEach(trigger => {
      trigger.addEventListener('pointerup', this.onTriggerClick);
    });

    window.addEventListener('pointerup', this.onClickOutside);
  }

  destroy() {
    this.closes.forEach(close => {
      close.removeEventListener('pointerup', this.onCloseClick);
    });
    window.removeEventListener('keyup', this.onKeyUp);
    this.triggers.forEach(trigger => {
      trigger.removeEventListener('pointerup', this.onTriggerClick);
    });
    window.removeEventListener('pointerup', this.onClickOutside);

    this.el = null;
    this.closes = null;
    this.triggers = null;
    this.expandables = null;
  }
}
